/**
 * 将站内发品表单（SHEIN 布局 Editor）组装为 Temu bg.goods.add / bg.glo.goods.add 业务参数。
 */

import { fixedUi } from '@/i18n/fixedUi';
import { TEMU_MODE_LOCAL, TEMU_MODE_SEMI, temuShopCapabilities } from '@/temu-platform/utils/temuShopCapabilities';
import { findCategoryPathIds } from '@/temu-platform/utils/temuCategoryTree';
import {
  buildTemuGoodsAddPayload,
} from '@/temu-platform/utils/buildTemuGoodsAddPayload';
import { buildTemuLocalGoodsV3Payload } from '@/temu-platform/utils/buildTemuLocalGoodsV3Payload';
import {
  buildTemuModelTryOnSizeOptions,
  isIllegalTemuTryOnSizeName,
  resolveGoodsModelTryOnSize,
} from '@/temu-platform/utils/temuOfficialSizeChart';
import {
  isBlankTemuAttributeValueId,
  resolveSkcColorAgainstTemplate,
} from '@/temu-platform/utils/temuAttributeTemplate';
import { isLikelyBrowserReachableThumb } from '@/temu-platform/modules/products/components/TemuProductThumb';
import { collectTemuSkcPreviewImgUrls } from '@/temu-platform/utils/temuProductImage';
import { isStrictTemuImageCdnUrl } from '@/pages/ipe/workbench/utils/convertListingImageToTemu';
import { isEditorAttrVisible, pruneProductAttributesForSubmit } from '@/temu-platform/utils/temuAttrVisibility';

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

/**
 * 依序取第一个正整数。
 * 平台规格 ID 可能从 temuSpecId / temu_spec_id / attribute_value_id 任一处来，
 * 且旧数据里 temuSpecId 常是空串或 0 —— 用 `??` 会把空串当有效值，必须先过滤。
 */
function firstPositiveInt(...values) {
  for (const raw of values) {
    const n = toInt(raw, 0);
    if (n > 0) return n;
  }
  return 0;
}

function platformLongId(raw) {
  const text = String(raw ?? '').trim();
  if (!/^\d+$/.test(text)) return 0;
  const value = Number(text);
  return Number.isSafeInteger(value) && value > 0 ? value : 0;
}

function toMoneyCents(yuan) {
  const n = Number(yuan);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** 提交平台必须用 Temu 图片 CDN；站内地址或带非法字符的 URL 会报域名不合法 */
function imageUrlForPublish(image) {
  if (!image) return '';
  const temuUrl = String(image.temuUrl || '').trim();
  return isStrictTemuImageCdnUrl(temuUrl) ? temuUrl : '';
}

/** 仅接受已转 Temu 图片 CDN 的图址（避免 materialImgUrl 误存 ipe/本地地址） */
function isTemuHostedImageUrl(url = '') {
  return isStrictTemuImageCdnUrl(url);
}

function publishImageUrl(imageOrUrl) {
  if (!imageOrUrl) return '';
  if (typeof imageOrUrl === 'string') {
    return isTemuHostedImageUrl(imageOrUrl) ? imageOrUrl.trim() : '';
  }
  const temu = imageUrlForPublish(imageOrUrl);
  if (temu) return temu;
  return '';
}

function imageUrlForListPreview(image) {
  if (!image) return '';
  const local = String(
    image.previewUrl
    || image.publicUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
  const temu = String(image.temuUrl || '').trim();
  if (local && isLikelyBrowserReachableThumb(local)) return local;
  if (temu && isLikelyBrowserReachableThumb(temu)) return temu;
  return local || temu || '';
}

function collectTemuPropDefs(attrTpl) {
  const map = new Map();
  const rows = [
    ...(attrTpl?.productAttrs || []),
    ...(attrTpl?.requiredProduct || []),
    ...(attrTpl?.optionalProduct || []),
    ...(attrTpl?.skcSaleAttrs || []),
    ...(attrTpl?.skuSaleAttrs || []),
  ];
  const rawInfos = attrTpl?.raw?.data?.[0]?.attribute_infos
    || attrTpl?.raw?.attribute_infos
    || [];
  for (const attr of rows) {
    const id = String(attr?.attributeId || '').trim();
    if (!id || map.has(id)) continue;
    const raw = (Array.isArray(rawInfos) ? rawInfos : []).find(
      (r) => String(r?.attribute_id ?? r?.attributeId) === id,
    );
    map.set(id, {
      attr,
      temuProp: raw?.temu_prop || attr?.temu_prop || null,
      rawValues: raw?.attribute_value_info_list || [],
    });
  }
  return map;
}

function findRawAttrValue(rawValues, valueId) {
  const id = String(valueId ?? '').trim();
  if (!id) return null;
  return (Array.isArray(rawValues) ? rawValues : []).find((v) => (
    String(v?.attribute_value_id ?? '') === id
    || String(v?.temu_vid ?? '') === id
    || String(v?.attributeValueId ?? '') === id
    // 品牌：模板常存 brandId，选项里 temu_brand_id / brandId 才是真正可匹配键
    || String(v?.temu_brand_id ?? '') === id
    || String(v?.brandId ?? '') === id
  )) || null;
}

function findEditorAttrValue(values, valueId) {
  const id = String(valueId ?? '').trim();
  if (!id) return null;
  return (Array.isArray(values) ? values : []).find((v) => (
    String(v?.id) === id
    || String(v?.brandId ?? '') === id
  )) || null;
}

/** 提交平台须用 attrs.get 原始属性值名（如 尼龙Nylon），勿用本地精简展示名 */
function officialPropValue(rawVal, editorVal, fallback = '') {
  return String(
    rawVal?.attribute_value
    || rawVal?.attribute_value_name
    || rawVal?.attributeValue
    || editorVal?.name
    || editorVal?.nameCn
    || fallback
    || '',
  ).trim();
}

/**
 * productPropertyReqs.valueUnit：原样透传 attrs 模板单位（如 g/m²、%）。
 * 成分无单位时兜底 "%"；勿把包装公制 cm/g 误塞进货品属性。
 */
export function resolveProductPropertyValueUnit(rawUnit, { isComposition = false } = {}) {
  const unit = String(rawUnit || '').trim();
  if (unit) return unit;
  if (isComposition) return '%';
  return '';
}

function isHandInputProductAttr(editorAttr, temuProp) {
  const mode = Number(editorAttr?.mode);
  // 单选/多选/成分：即使 controlType 异常也绝不当手输（否则会 vid=0，父属性丢值 → 6000002）
  if (mode === 1 || mode === 3 || mode === 4) return false;
  if (Array.isArray(editorAttr?.values) && editorAttr.values.length > 0) return false;
  if (mode === 0) return true;
  const controlType = Number(temuProp?.controlType);
  return controlType === 0;
}

/**
 * 将表单单个属性值解析为 Temu productPropertyReqs 条目。
 * - 手输（克重等）：vid 必须为 0，数字不可当属性值 id（否则会报 属性值名称有误）
 * - 下拉/成分：vid + 平台原始 propValue 必须一致
 */
export function resolveProductPropertyEntry(entry, { editorAttr, def } = {}) {
  const temuProp = def?.temuProp || editorAttr?.temu_prop || null;
  const values = def?.attr?.values || editorAttr?.values || [];
  const rawValues = def?.rawValues || [];
  const handInput = isHandInputProductAttr(editorAttr || def?.attr, temuProp);

  if (entry && typeof entry === 'object' && ('valueId' in entry || 'percent' in entry || 'attributeValueId' in entry)) {
    const valueId = entry.valueId ?? entry.attributeValueId ?? entry.vid;
    const rawVal = findRawAttrValue(rawValues, valueId);
    const opt = findEditorAttrValue(values, valueId);
    const vid = toInt(rawVal?.temu_vid ?? valueId, 0);
    const propValue = officialPropValue(rawVal, opt, entry.name || entry.label || entry.propValue || '');
    const numberInputValue = entry.percent != null ? String(entry.percent) : '';
    if (!propValue && !vid) return null;
    return { vid, propValue, numberInputValue };
  }

  const text = String(entry ?? '').trim();
  if (!text) return null;

  // 手输属性（克重等）：vid=0，数值只放 propValue；valueUnit 用模板单位（如 g/m²）。
  // numberInputValue 仅成分比例(controlType=16)使用，克重填了会报「数值录入信息必须为空」
  if (handInput) {
    return {
      vid: 0,
      propValue: text.replace(/\s*(g\/m²|g\/m2|gsm|g\/㎡)\s*$/i, '').trim() || text,
      numberInputValue: '',
    };
  }

  // 仅当该 id 确实是本属性的可选值时，才当作 vid（避免「180」克重误当 vid）
  const rawById = findRawAttrValue(rawValues, text);
  const optById = findEditorAttrValue(values, text);
  if (rawById || optById) {
    const vid = toInt(rawById?.temu_vid ?? optById?.id ?? text, 0);
    const propValue = officialPropValue(rawById, optById, '');
    if (!propValue && !vid) return null;
    return { vid, propValue, numberInputValue: '' };
  }

  const optByName = (values || []).find((v) => (
    String(v?.name || '') === text
    || String(v?.nameCn || '') === text
    || String(v?.label || '') === text
  ));
  if (optByName) {
    const rawVal = findRawAttrValue(rawValues, optByName.id);
    return {
      vid: toInt(rawVal?.temu_vid ?? optByName.id, 0),
      propValue: officialPropValue(rawVal, optByName, text),
      numberInputValue: '',
    };
  }

  // 自定义文本：无平台 vid
  return { vid: 0, propValue: text, numberInputValue: '' };
}

export function buildProductPropertyReqs(form, attrTpl) {
  const defs = collectTemuPropDefs(attrTpl);
  const out = [];
  const pool = [
    ...(attrTpl?.productAttrs || []),
    ...(attrTpl?.requiredProduct || []),
    ...(attrTpl?.optionalProduct || []),
  ];
  // 提交前级联清理不可见属性（平台当前父取值下不展示的项，如针织下的面料纹理）及其依赖
  const attrs = pruneProductAttributesForSubmit(
    form.productAttributes && typeof form.productAttributes === 'object'
      ? form.productAttributes
      : {},
    pool,
  );
  // 不可见属性勿提交
  Object.entries(attrs).forEach(([attributeId, value]) => {
    if (value == null || value === '') return;
    const editorAttr = pool.find((a) => String(a?.attributeId) === String(attributeId));
    if (editorAttr && !isEditorAttrVisible(editorAttr, attrs, pool)) return;
    const def = defs.get(String(attributeId)) || {
      attr: editorAttr,
      temuProp: editorAttr?.temu_prop || null,
      rawValues: [],
    };
    const temuProp = def?.temuProp || editorAttr?.temu_prop || null;
    if (temuProp?.isSale) return; // 销售属性走 spec
    const values = Array.isArray(value) ? value : [value];
    values.forEach((entry) => {
      const resolved = resolveProductPropertyEntry(entry, { editorAttr: editorAttr || def?.attr, def });
      if (!resolved) return;
      const { vid, propValue, numberInputValue } = resolved;
      if (!propValue && !vid) return;
      const isComposition = Boolean(
        editorAttr?.isComposition
        || Number(editorAttr?.mode) === 4
        || Number(temuProp?.controlType) === 16
        || numberInputValue,
      );
      out.push({
        templatePid: toInt(temuProp?.templatePid ?? attributeId),
        pid: toInt(temuProp?.pid ?? attributeId),
        refPid: toInt(temuProp?.refPid),
        propName: String(temuProp?.name || def?.attr?.name || editorAttr?.name || ''),
        vid,
        propValue,
        valueUnit: resolveProductPropertyValueUnit(temuProp?.valueUnit, { isComposition }),
        numberInputValue,
        controlType: temuProp?.controlType,
      });
    });
  });
  return out;
}

function buildSpecPropertyReqs(form, attrTpl) {
  const out = [];
  const colorAttr = attrTpl?.skcSaleAttrs?.[0];
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0];
  const defs = collectTemuPropDefs(attrTpl);

  const pushSpec = (attrDef, valueId, label) => {
    if (!attrDef) return;
    const id = String(attrDef.attributeId);
    const def = defs.get(id);
    const temuProp = def?.temuProp;
    const rawVal = (def?.rawValues || []).find((v) => String(v.attribute_value_id) === String(valueId));
    out.push({
      templatePid: toInt(temuProp?.templatePid ?? id),
      pid: toInt(temuProp?.pid ?? id),
      refPid: toInt(temuProp?.refPid),
      propName: String(temuProp?.name || attrDef.name || ''),
      vid: toInt(rawVal?.temu_vid ?? valueId, 0),
      propValue: String(label || rawVal?.attribute_value || ''),
      valueUnit: String(temuProp?.valueUnit || ''),
      specId: toInt(rawVal?.temu_spec_id ?? valueId, 0),
      parentSpecId: toInt(temuProp?.parentSpecId, 0),
      valueGroupId: toInt(rawVal?.temu_value_group_id, 0),
      valueGroupName: String(rawVal?.temu_value_group_name || ''),
    });
  };

  (form.skcList || []).forEach((skc) => {
    const resolved = resolveSkcColorAgainstTemplate(skc, colorAttr);
    if (!isBlankTemuAttributeValueId(resolved.colorValueId)) {
      pushSpec(colorAttr, resolved.colorValueId, resolved.colorLabel);
    }
    (skc.skuRows || []).forEach((sku) => {
      if (!isBlankTemuAttributeValueId(sku.sizeValueId)) {
        pushSpec(sizeAttr, sku.sizeValueId, sku.sizeLabel);
      }
    });
  });

  // 去重
  const seen = new Set();
  return out.filter((row) => {
    const key = `${row.templatePid}|${row.specId}|${row.vid}|${row.propValue}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function collectCarouselUrls(form) {
  const urls = [];
  const push = (img) => {
    const u = imageUrlForPublish(img);
    if (u && !urls.includes(u)) urls.push(u);
  };
  (form.skcList || []).forEach((skc) => {
    push(skc.mainImage);
    (skc.detailImages || []).forEach(push);
    Object.values(skc.showroomSlots || {}).forEach(push);
  });
  (form.carouselImages || []).forEach(push);
  return urls;
}

/** 素材图：优先手传槽位；未传时由 ensureTemuMaterialFromMainOnForm 从主图裁切补齐 */
function resolveMaterialImgUrl(form) {
  const fromSlot = publishImageUrl(form.materialImage);
  if (fromSlot) return fromSlot;
  return publishImageUrl(String(form.materialImgUrl || '').trim());
}

/** 主图视频 → productCarouseVideoReqList（可选；Temu 要求必带平台 vid/videoUrl） */
function buildProductCarouseVideoReqList(form) {
  return (Array.isArray(form.temuVideos) ? form.temuVideos : [])
    .filter((v) => v && typeof v === 'object')
    .map((v) => {
      const vid = String(v.vid || '').trim();
      const videoUrl = String(v.videoUrl || '').trim();
      // vid 为平台转传后返回的视频 ID，官方文档中为必填；仅有 ipe 预览地址不算数
      if (!vid || !videoUrl) return null;
      return {
        vid,
        coverUrl: String(v.coverUrl || '').trim(),
        videoUrl,
        width: toInt(v.width, 0),
        height: toInt(v.height, 0),
      };
    })
    .filter(Boolean);
}

function resolveCatIds(form) {
  if (Array.isArray(form.catPathIds) && form.catPathIds.length) {
    return form.catPathIds.map((id) => toInt(id)).filter((id) => id > 0);
  }
  if (Array.isArray(form.categoryPathIds) && form.categoryPathIds.length) {
    return form.categoryPathIds.map((id) => toInt(id)).filter((id) => id > 0);
  }
  const fromTree = findCategoryPathIds(form.categoryTree || form.categories, form.categoryId);
  if (fromTree.length) return fromTree.map((id) => toInt(id)).filter((id) => id > 0);
  const leaf = toInt(form.categoryId);
  return leaf > 0 ? [leaf] : [];
}

function buildProductSkcReqs(form, attrTpl, opts) {
  const semiManaged = Boolean(opts.semiManaged);
  const colorAttr = attrTpl?.skcSaleAttrs?.[0];
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0];
  const clothing = Boolean(form.clothingCategory) || Boolean(colorAttr);
  const carousel = collectCarouselUrls(form);
  const defs = collectTemuPropDefs(attrTpl);

  const skcSource = (form.skcList || []).filter((skc) => (
    !isBlankTemuAttributeValueId(skc?.colorValueId)
    || String(skc?.colorExtraValue || skc?.colorLabel || '').trim()
    || !clothing
  ));

  const list = (skcSource.length ? skcSource : [{}]).map((skc, skcIndex) => {
    const resolved = resolveSkcColorAgainstTemplate(skc, colorAttr);
    const skcPreviewUrls = clothing
      ? collectTemuSkcPreviewImgUrls(skc, imageUrlForPublish)
      : [];
    const preview = skcPreviewUrls[0]
      || imageUrlForPublish(skc.mainImage)
      || carousel[0]
      || '';
    const colorBlockUrl = imageUrlForPublish(skc.colorBlockImage);
    let mainProductSkuSpecReqs;
    if (clothing && colorAttr && !isBlankTemuAttributeValueId(resolved.colorValueId)) {
      const def = defs.get(String(colorAttr.attributeId));
      const rawVal = (def?.rawValues || []).find(
        (v) => String(v.attribute_value_id) === String(resolved.colorValueId),
      );
      const specId = firstPositiveInt(
        resolved.temuSpecId,
        rawVal?.temu_spec_id,
        rawVal?.attribute_value_id,
        resolved.colorValueId,
      );
      if (!(specId > 0)) {
        throw new Error(`颜色「${resolved.colorLabel || resolved.colorExtraValue || '未命名'}」缺少平台规格 ID，请重新选择或创建颜色规格`);
      }
      mainProductSkuSpecReqs = [{
        parentSpecId: toInt(def?.temuProp?.parentSpecId, 0),
        parentSpecName: String(def?.temuProp?.name || colorAttr.name || fixedUi("颜色")),
        specId,
        specName: String(resolved.colorLabel || rawVal?.attribute_value || ''),
      }];
    } else if (clothing) {
      throw new Error(
        `颜色「${resolved.colorLabel || resolved.colorExtraValue || '未命名'}」尚未绑定平台规格，请从属性选项选择或先创建自定义规格`,
      );
    } else {
      mainProductSkuSpecReqs = [{ parentSpecId: 0, parentSpecName: '', specId: 0, specName: '' }];
    }

    const skuRows = (skc.skuRows || []).length
      ? skc.skuRows
      : [{ supplierSku: '' }];
    const skcExtCode = String(resolved.supplierCode || form.supplierCode || '').trim();

    const productSkuReqs = skuRows.map((sku, skuIndex) => {
      const priceYuan = sku.supplyPrice ?? sku.price ?? form.supplyPrice ?? form.defaultPriceYuan ?? 0;
      const stock = sku.stock ?? form.stockQty ?? form.defaultStock ?? 0;
      const specReqs = [];
      if (clothing && colorAttr && !isBlankTemuAttributeValueId(resolved.colorValueId)) {
        const def = defs.get(String(colorAttr.attributeId));
        const rawVal = (def?.rawValues || []).find(
          (v) => String(v.attribute_value_id) === String(resolved.colorValueId),
        );
        specReqs.push({
          parentSpecId: toInt(def?.temuProp?.parentSpecId, 0),
          parentSpecName: String(def?.temuProp?.name || colorAttr.name || ''),
          specId: firstPositiveInt(
            resolved.temuSpecId,
            rawVal?.temu_spec_id,
            rawVal?.attribute_value_id,
            resolved.colorValueId,
          ),
          specName: String(resolved.colorLabel || ''),
        });
      }
      if (sizeAttr && !isBlankTemuAttributeValueId(sku.sizeValueId)) {
        const def = defs.get(String(sizeAttr.attributeId));
        const rawVal = (def?.rawValues || []).find(
          (v) => String(v.attribute_value_id) === String(sku.sizeValueId),
        );
        specReqs.push({
          parentSpecId: toInt(def?.temuProp?.parentSpecId, 0),
          parentSpecName: String(def?.temuProp?.name || sizeAttr.name || ''),
          specId: firstPositiveInt(rawVal?.temu_spec_id, rawVal?.attribute_value_id, sku.sizeValueId),
          specName: String(sku.sizeLabel || rawVal?.attribute_value || ''),
        });
      }

      const skcCarouselFirst = skcPreviewUrls[0] || preview;
      const productSkuId = platformLongId(sku.skuCode || sku.productSkuId);
      const base = {
        ...(productSkuId ? { productSkuId } : {}),
        extCode: String(
          sku.supplierSku
          || (skcExtCode ? `${skcExtCode}-${skuIndex + 1}` : `SKU-${skcIndex + 1}-${skuIndex + 1}`),
        ),
        currencyType: String(sku.currency || form.currency || 'USD'),
        // 服饰 2000015：sku 预览图必须等于 skc 轮播第一张；勿传色块图
        thumbUrl: clothing
          ? skcCarouselFirst
          : (imageUrlForPublish(sku.skuImage) || colorBlockUrl || preview),
        productSkuSpecReqs: specReqs,
        supplierPriceYuan: priceYuan,
        stock,
        lengthMm: toInt(Number(sku.length || form.pkgLength || 10) * 10),
        widthMm: toInt(Number(sku.width || form.pkgWidth || 10) * 10),
        heightMm: toInt(Number(sku.height || form.pkgHeight || 5) * 10),
        weightMg: toInt(Number(sku.weight || form.weightGrams || 100) * 1000),
        // 站内固定公制 cm/g，一并带上卖家中心同款 input*
        length: sku.length || form.pkgLength || '',
        width: sku.width || form.pkgWidth || '',
        height: sku.height || form.pkgHeight || '',
        weightUi: sku.weight || form.weightGrams || '',
        // 货品净重（g）→ WhExtAttr.productSkuNetWeightReq（mg）；与净含量无关
        netWeight: sku.netWeight || form.netWeightGrams || '',
        // 净含量 → MultiPack.productSkuNetContentReq；勿用 netWeight 顶替
        netContent: sku.netContent ?? sku.netContentNumber ?? '',
        netContentNumber: sku.netContentNumber ?? sku.netContent ?? '',
        netContentUnitCode: sku.netContentUnitCode || '',
        skuClassification: sku.skuClassification || 1,
        numberOfPieces: sku.numberOfPieces ?? sku.quantity ?? 1,
        quantity: sku.quantity,
        quantityUnit: sku.quantityUnit || sku.pieceUnitCode || 1,
        pieceUnitCode: sku.pieceUnitCode || sku.quantityUnit || 1,
        totalContains: sku.totalContains,
        individuallyPacked: sku.individuallyPacked,
        isSensitive: sku.isSensitive != null ? sku.isSensitive : (form.isSensitive ?? 0),
        sensitiveList: Array.isArray(sku.sensitiveList) ? sku.sensitiveList : [],
        suggestedPrice: sku.suggestedPrice,
        suggestedPriceCurrencyType: sku.suggestedPriceCurrencyType || form.currency || 'USD',
        accessories: Array.isArray(sku.accessories) ? sku.accessories : [],
      };
      return base;
    });

    const productSkcId = platformLongId(skc.skcName || skc.productSkcId);
    return {
      ...(productSkcId ? { productSkcId } : {}),
      extCode: skcExtCode || `SKC-${skcIndex + 1}`,
      // 服饰：SKC 轮播 = 主图 + 细节图（平台要求 ≥5）；非服饰可单张或不传
      previewImgUrls: clothing
        ? (skcPreviewUrls.length ? skcPreviewUrls : (preview ? [preview] : []))
        : (preview ? [preview] : carousel.slice(0, 1)),
      mainProductSkuSpecReqs,
      productSkuReqs,
    };
  });

  // 非服饰：强制单 SKC
  if (!clothing && list.length > 1) {
    return [list[0]];
  }
  return list;
}

export function samplePublishPayloadCurrency(payload) {
  const sku = payload?.productSkcReqs?.[0]?.productSkuReqs?.[0];
  if (!sku) return '';
  if (sku.currencyType) return String(sku.currencyType);
  if (Array.isArray(sku.siteSupplierPrices) && sku.siteSupplierPrices.length) return 'USD';
  if (sku.supplierPrice != null) return 'USD';
  return '';
}

/**
 * @param {object} form Editor 表单
 * @param {object} options
 */
export function buildTemuPublishPayload(form, {
  shopType = '',
  modeCaps = null,
  siteList = null,
  attrTpl = null,
  categories = null,
  /** 复色（编辑已有货品）：不改标题，不校验也不下发商品名 */
  omitTitle = false,
  /**
   * 编辑已有货品只提交变更内容：未改动的标题/素材图/外包装沿用平台原值，
   * 不做必填校验，也不写进签名体（平台侧保留原值）。
   */
  partialEdit = false,
} = {}) {
  const shopCaps = modeCaps || temuShopCapabilities(shopType || form?.shopType);
  const isLocal = shopCaps?.mode === TEMU_MODE_LOCAL
    || /本土|local/i.test(String(shopType || form?.shopType || ''));
  const semiManaged = shopCaps?.mode === TEMU_MODE_SEMI
    || /半托/i.test(String(shopType || form?.shopType || ''));
  const skipSiteWarehouseFreight = Boolean(shopCaps?.skipSiteWarehouseFreight);

  const catIds = resolveCatIds({
    ...form,
    categoryTree: categories || form.categoryTree,
    categories: categories || form.categories,
  });
  // 本土 V3 可用 extCatName 推荐类目；有类目 ID 更好，但非硬前置
  if (!catIds.length && !isLocal) {
    throw new Error('请先选择叶子类目');
  }

  const productName = String(form.title || form.productName || '').trim();
  const skipTitle = omitTitle || partialEdit;
  if (!productName && !skipTitle) {
    throw new Error('请填写商品名称');
  }
  const titleEn = String(form.titleEn || form.englishTitle || '').trim();
  if (!titleEn && !isLocal && !skipTitle) {
    throw new Error('请填写英文名称');
  }

  if (isLocal) {
    return buildTemuLocalGoodsV3Payload({
      ...form,
      productName,
      titleEn,
      catIds,
      categoryTree: categories || form.categoryTree,
      categories: categories || form.categories,
    }, { attrTpl, categories });
  }

  const sizeIds = Array.isArray(form.temuSizeTemplateIds)
    ? form.temuSizeTemplateIds.map((id) => toInt(id)).filter((id) => id > 0)
    : [];

  const selectedSiteIds = skipSiteWarehouseFreight
    ? []
    : (Array.isArray(form.selectedSites) ? form.selectedSites : [])
      .map((id) => toInt(id))
      .filter((id) => id > 0);
  const siteIds = skipSiteWarehouseFreight
    ? []
    : (Array.isArray(form.temuSiteIds) ? form.temuSiteIds : [])
      .map((id) => toInt(id))
      .filter((id) => id > 0);
  const resolvedSiteIds = siteIds.length ? siteIds : selectedSiteIds;
  const siteId = skipSiteWarehouseFreight
    ? 0
    : toInt(
      form.temuSiteId
      || resolvedSiteIds[0]
      || form.siteId,
    );

  const materialImgUrl = resolveMaterialImgUrl(form);
  if (!materialImgUrl && !isLocal && !partialEdit) {
    throw new Error('请上传素材图，或确保主图已上传以便自动裁成 1:1 素材图');
  }

  const productCarouseVideoReqList = buildProductCarouseVideoReqList(form);
  const rawVideos = Array.isArray(form.temuVideos) ? form.temuVideos : [];
  if (rawVideos.length && !productCarouseVideoReqList.length && !partialEdit) {
    throw new Error('主图视频未完成 Temu 上传（缺少 vid/videoUrl），请重新上传后再发品');
  }

  const originRegion = String(form.originRegion || form.originCountry || 'CN').trim().toUpperCase() || 'CN';
  const originRegion2Id = form.originRegion2Id || form.region2Id || '';

  const goodsModelsRaw = Array.isArray(form.goodsModels) ? form.goodsModels : [];
  const tryOnSizeOptions = buildTemuModelTryOnSizeOptions({
    sizeAttr: attrTpl?.skuSaleAttrs?.[0] || null,
    attrTpl,
    selectedSizeIds: form.selectedSizeIds,
    skcList: form.skcList,
  });
  const goodsModels = goodsModelsRaw.map((model) => {
    if (!model || typeof model !== 'object') return model;
    const resolved = resolveGoodsModelTryOnSize(model.sizeSpecName, tryOnSizeOptions);
    if (resolved) {
      return {
        ...model,
        sizeSpecId: resolved.sizeSpecId,
        sizeSpecName: resolved.sizeSpecName,
        sizeValueId: resolved.sizeValueId,
      };
    }
    if (isIllegalTemuTryOnSizeName(model.sizeSpecName)) {
      throw new Error('模特试穿尺码不可为均码，请从商品已选尺码中选择');
    }
    return model;
  });

  const payload = buildTemuGoodsAddPayload({
    catIds,
    productName,
    productPropertyReqs: buildProductPropertyReqs(form, attrTpl),
    productSpecPropertyReqs: buildSpecPropertyReqs(form, attrTpl),
    productSkcReqs: buildProductSkcReqs(form, attrTpl, { semiManaged }),
    carouselImageUrls: collectCarouselUrls(form),
    materialImgUrl,
    productCarouseVideoReqList,
    clothingCategory: Boolean(attrTpl?.skcSaleAttrs?.length),
    defaultPriceYuan: form.supplyPrice ?? form.defaultPriceYuan,
    defaultStock: form.stockQty ?? form.defaultStock,
    originRegion,
    originRegion2Id,
    isSensitive: form.isSensitive ?? 0,
    siteId: siteId || undefined,
    siteIds: resolvedSiteIds.length ? resolvedSiteIds : (siteId ? [siteId] : []),
    warehouseId: skipSiteWarehouseFreight ? '' : (form.warehouseId || ''),
    freightTemplateId: skipSiteWarehouseFreight
      ? ''
      : (form.freightTemplateId || form.temuFreightTemplateId || ''),
    shipmentLimitSecond: skipSiteWarehouseFreight
      ? undefined
      : (form.shipmentLimitSecond || 86400 * 2),
    isCustomProduct: Number(form.isCustomProduct ?? form.personalizationSwitch) === 1 ? 1 : 0,
    personalizationSwitch: Number(form.isCustomProduct ?? form.personalizationSwitch) === 1 ? 1 : 0,
    customFirstType: form.customFirstType || form.customizedTechnologyReq?.firstType || '',
    customTechnologyType: form.customTechnologyType || form.customizedTechnologyReq?.technologyType || '',
    customTwiceTypes: Array.isArray(form.customTwiceTypes)
      ? form.customTwiceTypes
      : (Array.isArray(form.customizedTechnologyReq?.twiceType)
        ? form.customizedTechnologyReq.twiceType
        : []),
    customManufacturingLocations: Array.isArray(form.customManufacturingLocations)
      ? form.customManufacturingLocations
      : (Array.isArray(form.customizedTechnologyReq?.manufacturingLocation?.region1ShortNames)
        ? form.customizedTechnologyReq.manufacturingLocation.region1ShortNames
        : []),
    customizedTechnologyReq: form.customizedTechnologyReq || undefined,
    sizeTemplateIds: sizeIds,
    showSizeTemplateIds: sizeIds,
    goodsModels,
    productI18nReqs: omitTitle || !titleEn ? [] : [{ language: 'en', productName: titleEn }],
    guideFiles: Array.isArray(form.guideFiles) ? form.guideFiles : [],
    originCertFiles: Array.isArray(form.originCertFiles) ? form.originCertFiles : [],
    packageShape: form.packageShape || '',
    packageTypeOuter: form.packageTypeOuter || '',
    outerPackageImages: Array.isArray(form.outerPackageImages) ? form.outerPackageImages : [],
    productOuterPackageImageReqs: Array.isArray(form.productOuterPackageImageReqs)
      ? form.productOuterPackageImageReqs
      : [],
  }, { semiManaged, partialEdit });

  // 复色只改规格与图片，标题沿用平台原值：不下发商品名/多语名，避免因标题缺失或被判重被拒
  if (omitTitle || partialEdit) {
    delete payload.productName;
    delete payload.productI18nReqs;
  }

  // 草稿/列表预览图勿写入 Temu 签名体（未知字段可能被拒）
  return payload;
}

/**
 * 半托管 / 全托管编辑：货品体与 bg.glo.goods.add 相同，并带已有 productId、SKC、SKU。
 * 后端再走 发起修改单 → bg.glo.goods.update → 提交修改单。
 */
export function buildTemuGoodsUpdatePayload(form, options = {}) {
  const productId = platformLongId(form?.spuName || form?.productId);
  if (!productId) {
    throw new Error('编辑商品缺少平台货品 ID，无法提交改品');
  }
  const payload = buildTemuPublishPayload(form, options);
  payload.productId = productId;
  return payload;
}
